import React, { useState, useEffect, useRef, useCallback } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
  IconButton,
  Typography,
  Grid,
  Paper,
  Button,
  Box,
  Card,
  CardContent
} from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import LoadingButton from '../components/LoadingButton'
import LoadingCircle from '../components/common/LoadingCircle'
import { smallScreenWidth } from '../constants/AppConstants'
import OpenSearchDropdown from '../components/OpenSearchDropdown'
import { AntTabs } from '../components/VendorTabs'
import { debounce } from 'lodash'
import VendorAgreementMain from '../components/VendorAgreementMain'
import VendorAgreementParticipants from '../components/VendorAgreementParticipants'
import VendorAgreementLinesTable from '../components/VendorAgreementLines'
import fetchFromAthena from '../functions/FetchFromAthena'
import CommentDialog from '../components/CommentDialog'

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    margin: '2rem 1rem'
  },
  padding: {
    padding: theme.spacing(1)
  },
  demo1: {
    backgroundColor: theme.palette.background.paper
  }
}))

const defaultHeaders = {
  procureGroup: '',
  agreementType: '',
  agreementRef: ''
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vendor-agreement-tabpanel-${index}`}
      aria-labelledby={`vendor-agreement-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function VendorAgreement(props) {
  const [state, setState] = useState({
    isLoading: false,
    fetched: true,
    tableKey: JSON.stringify(defaultHeaders)
  })
  const [showTable, setShowTable] = useState(false)
  const [headers, setHeaders] = useState(defaultHeaders)
  const [clearHeader, setClearHeaders] = useState(false)
  const [tabValue, setTabValue] = useState(0)
  const [tableRefreshKey, setTableRefreshKey] = useState(0)
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)
  const [agreementData, setAgreementData] = useState({})
  const [isLoadingMainData, setIsLoadingMainData] = useState(false)
  const [procureGroup, setProcureGroup] = useState('')
  const [agreementType, setAgreementType] = useState('')
  const [agreementRef, setAgreementRef] = useState('')
  const [procureGroupDisplay, setProcureGroupDisplay] = useState('')
  const [agreementTypeDisplay, setAgreementTypeDisplay] = useState('')
  const [agreementRefDisplay, setAgreementRefDisplay] = useState('')
  const [clearTrigger, setClearTrigger] = useState(false)
  const [loadedTabs, setLoadedTabs] = useState(new Set())
  const [loadingTabs, setLoadingTabs] = useState(new Set())
  const [showHeaders, setShowHeaders] = React.useState(true)

  const classes = useStyles()

  const { credentials } = props.fetchInitialData

  // Refs to track state for preventing duplicate calls
  const headersRef = useRef(headers)
  const loadedTabsRef = useRef(new Set())
  const loadingTabsRef = useRef(new Set())

  // Keep refs in sync with state
  useEffect(() => {
    headersRef.current = headers
  }, [headers])

  useEffect(() => {
    loadedTabsRef.current = loadedTabs
  }, [loadedTabs])

  useEffect(() => {
    loadingTabsRef.current = loadingTabs
  }, [loadingTabs])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const procureGroup = urlParams.get('procureGroup')
    const agreementType = urlParams.get('agreementType')
    const agreementRef = urlParams.get('agreementRef')

    if (procureGroup && agreementType && agreementRef) {
      setHeaders({ procureGroup, agreementType, agreementRef})
      setShowTable(true)
      setShowHeaders(false)
    }
  }, [])
  // Create a debounced submit handler
  const debouncedSubmit = React.useCallback(
    debounce((e) => {
      setShowTable(true)
      setState(prevState => ({ ...prevState, tableKey: JSON.stringify(headers) }))
      setTableRefreshKey(prevKey => prevKey + 1)
      
      // Re-enable the button after 2 seconds
      setTimeout(() => {
        setIsSubmitDisabled(false)
      }, 2000)
    }, 300),
    [headers]
  )

  const handleSubmit = e => {
    e.preventDefault()
    if (!isSubmitDisabled) {
      setIsSubmitDisabled(true)
      setState(prevState => ({ ...prevState, isLoading: true }))
      debouncedSubmit(e)
      // Loading state will be cleared when main tab API call completes in loadTabData
    }
  }

  const handleCallback = (values, error) => {
    if (error) {
      setState({ ...state, isLoading: false })
      setShowTable(false)
      return null
    }
    setState({ ...state, isLoading: false })
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Function to load tab data lazily
  const loadTabData = useCallback(async (tabValue) => {
    const currentHeaders = headersRef.current
    if (!currentHeaders.procureGroup || !currentHeaders.agreementType || !currentHeaders.agreementRef) {
      return
    }

    // Prevent duplicate calls
    if (loadedTabsRef.current.has(tabValue) || loadingTabsRef.current.has(tabValue)) {
      return
    }

    // Mark tab as loading
    setLoadingTabs(prev => new Set([...prev, tabValue]))

    try {
      if (tabValue === 0) {
        // Main tab - fetch agreement data
        setIsLoadingMainData(true)
        const queries = ['po_vendor_agreement']
        
        const data = await Promise.allSettled(
          queries.map(query =>
            fetchFromAthena({
              queryName: query,
              headers: currentHeaders,
              accessToken: credentials.user.accessToken,
              credentials: credentials
            })
          )
        )

        // Set data first
        setAgreementData(data?.[0]?.value?.[0] || {})
        // Mark tab as loaded
        setLoadedTabs(prev => new Set([...prev, tabValue]))
        // Only set loading to false after data is set and tab is marked as loaded
        setIsLoadingMainData(false)
        // Clear submit button loading state
        setState(prevState => ({ ...prevState, isLoading: false }))
        setIsSubmitDisabled(false)
        // Remove from loadingTabs after everything is complete
        setLoadingTabs(prev => {
          const next = new Set(prev)
          next.delete(tabValue)
          return next
        })
      } else {
        // For tabs 1 and 2 (Participants and Lines), just mark as loaded
        // The DashboardAthenaTable components will handle their own data fetching when they mount
        setLoadedTabs(prev => new Set([...prev, tabValue]))
        // Remove from loadingTabs
        setLoadingTabs(prev => {
          const next = new Set(prev)
          next.delete(tabValue)
          return next
        })
      }
    } catch (error) {
      console.error("Error fetching tab data:", error)
      if (tabValue === 0) {
        setAgreementData({})
        setIsLoadingMainData(false)
        // Clear submit button loading state on error
        setState(prevState => ({ ...prevState, isLoading: false }))
        setIsSubmitDisabled(false)
      }
      // Remove from loadingTabs on error
      setLoadingTabs(prev => {
        const next = new Set(prev)
        next.delete(tabValue)
        return next
      })
    }
  }, [credentials])

  // Load tab data when tab is selected
  useEffect(() => {
    if (showTable && !loadedTabsRef.current.has(tabValue) && !loadingTabsRef.current.has(tabValue)) {
      loadTabData(tabValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, showTable])

  // Reset loaded tabs when headers change (new agreement selected)
  useEffect(() => {
    if (showTable && tableRefreshKey > 0) {
      const currentHeaders = headersRef.current
      setLoadedTabs(new Set())
      setLoadingTabs(new Set())
      setAgreementData({})
      setIsLoadingMainData(false)
      // Reset to first tab
      setTabValue(0)
      // Clear refs synchronously so loadTabData(0) doesn't think tab 0 is already loaded
      // (refs are updated in other useEffects and would still have stale values here)
      loadedTabsRef.current = new Set()
      loadingTabsRef.current = new Set()
      // Load first tab data if headers are valid
      if (currentHeaders.procureGroup && currentHeaders.agreementType && currentHeaders.agreementRef) {
        loadTabData(0)
      } else {
        // Clear loading state if headers are invalid
        setState(prevState => ({ ...prevState, isLoading: false }))
        setIsSubmitDisabled(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableRefreshKey, showTable])

  if (!state.fetched) {
    return <LoadingCircle />
  }

  return (
    <div style={{ padding: '1.5rem 0.5rem' }}>
      <IconButton
        style={{ borderRadius: '5px', position: 'absolute' }}
        size='small'
        onClick={() => props.history.push('/')}
        aria-label='back'
      >
        <ArrowBackIcon />
        {props.fetchInitialData.credentials.appWidth > smallScreenWidth &&
          'Dashboard'}
      </IconButton>
      <Typography variant='h5' style={{ textAlign: 'center' }}>
        Vendor Agreement
      </Typography>
      {showHeaders && (
      <div style={{ margin: '1rem' }}>
        <Paper style={{ padding: '1rem' }}>
          <Grid container spacing={1}>
            <Grid item xs={12} style={{ position: 'relative' }}>
              <div style={{ margin: '0 1rem 0 0' }}>
                <Grid
                  container
                  alignContent='center'
                  alignItems='flex-start'
                  spacing={2}
                >
                  <Grid item>
                    <OpenSearchDropdown
                      id='procureGroup'
                      required
                      width='250px'
                      label='Procurement Group'
                      page="VENDORAGREEMENT"
                      entity="PROCURE_GROUP"
                      searchFields={['DISPLAY']}
                      accessToken={credentials.user.accessToken}
                      credentials={credentials}
                      clearTrigger={clearTrigger}
                      onChange={(val) => {
                        const procureGroupValue = val?.VALUE || null
                        const procureGroupDisplayValue = val?.DISPLAY || ''
                        setProcureGroup(procureGroupValue)
                        setProcureGroupDisplay(procureGroupDisplayValue)
                        setHeaders({ ...headers, procureGroup: procureGroupValue })
                        // Clear dependent fields when procurement group changes
                        if (!procureGroupValue) {
                          setAgreementType('')
                          setAgreementRef('')
                          setAgreementTypeDisplay('')
                          setAgreementRefDisplay('')
                          setHeaders({ ...headers, procureGroup: '', agreementType: '', agreementRef: '' })
                        }
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <OpenSearchDropdown
                      id='agreementType'
                      required
                      disabled={!procureGroup}
                      width='250px'
                      label='Agreement Type'
                      page="VENDORAGREEMENT"
                      entity="TYPE"
                      searchFields={['DISPLAY']}
                      accessToken={credentials.user.accessToken}
                      credentials={credentials}
                      clearTrigger={clearTrigger}
                      onChange={(val) => {
                        const agreementTypeValue = val?.VALUE || null
                        const agreementTypeDisplayValue = val?.DISPLAY || ''
                        setAgreementType(agreementTypeValue)
                        setAgreementTypeDisplay(agreementTypeDisplayValue)
                        setHeaders({ ...headers, agreementType: agreementTypeValue })
                        // Clear reference when type changes
                        if (!agreementTypeValue) {
                          setAgreementRef('')
                          setAgreementRefDisplay('')
                          setHeaders({ ...headers, agreementType: '', agreementRef: '' })
                        }
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <OpenSearchDropdown
                      id='agreementRef'
                      required
                      disabled={!procureGroup || !agreementType}
                      width='300px'
                      label='Reference'
                      page="VENDORAGREEMENT"
                      entity="REFERENCE"
                      searchFields={['DISPLAY']}
                      accessToken={credentials.user.accessToken}
                      credentials={credentials}
                      clearTrigger={clearTrigger}
                      filters={{
                        PROCURE_GROUP: procureGroup,
                        AGMT_TYPE: agreementType
                      }}
                      onChange={(val) => {
                        const agreementRefValue = val?.VALUE || null
                        const agreementRefDisplayValue = val?.DISPLAY || ''
                        setAgreementRef(agreementRefValue)
                        setAgreementRefDisplay(agreementRefDisplayValue)
                        setHeaders({ ...headers, agreementRef: agreementRefValue })
                      }}
                    />
                  </Grid>
                </Grid>
              </div>
            </Grid>
            <Grid item xs={12}>
              <div style={{ marginTop: '1rem' }}>
                <form
                  onSubmit={e => {
                    e.preventDefault()
                    handleSubmit(e)
                  }}
                  style={{ display: 'inline' }}
                >
                  <LoadingButton
                    disabled={!procureGroup || !agreementType || !agreementRef || isSubmitDisabled}
                    label='Submit'
                    isLoading={state.isLoading}
                    color='primaryVLButton'
                    buttonType='submit'
                  />
                </form>
                {!!(
                  procureGroup ||
                  agreementType ||
                  agreementRef
                ) && (
                  <Button
                    onClick={() => {
                      setClearHeaders(!clearHeader)
                      setHeaders(defaultHeaders)
                      setProcureGroup('')
                      setAgreementType('')
                      setAgreementRef('')
                      setProcureGroupDisplay('')
                      setAgreementTypeDisplay('')
                      setAgreementRefDisplay('')
                      setShowTable(false)
                      setState({ ...state, tableKey: JSON.stringify(defaultHeaders) })
                      setClearTrigger(prev => !prev)
                    }}
                    style={{ marginLeft: '0.5rem' }}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </Grid>
          </Grid>
        </Paper>
      </div>
      )}
      {showTable && loadedTabs.has(0) && !isLoadingMainData && (
        <Card style={{ margin: '1rem' }}>
          <CardContent style={{ position: 'relative' }}>
            <div>
              <>
                <div
                  style={{
                    display: 'flex',
                    margin: '1rem',
                    justifyContent: 'flex-start'
                  }}
                >
                  <div>
                  <Typography variant='h5'>{agreementData.VEN_AGRMT_REF}</Typography>
                  <Typography variant='subtitle1'>{`Procure Group: ${agreementData.PROCURE_GROUP}`}</Typography>
                  <Typography variant='subtitle1'>{`Agreement Type: ${agreementData.AGMT_TYPE}`}</Typography>
                    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', alignItems: 'end', gap: '0.25rem' }}>
                        <span className={classes.descript}>{`Comments:  `}</span>
                        <span className={classes.descriptVal}>
                          {agreementData.COMMENT_INDEX ? (
                            <CommentDialog
                              {...props}
                              erp="lawson"
                              text="view"
                              fileName={agreementData.COMMENT_FILENAME}
                              index={agreementData.COMMENT_INDEX}
                            />
                          ) : 'N/A'}
                        </span>
                      </div>
                  </div>
                </div>
                <div className={classes.demo1}>
                  <AntTabs
                    value={tabValue}
                    color={credentials.primaryAppColor || "#1976d2"}
                    handleChange={handleTabChange}
                    controlledTabs
                    tabs={[
                      { display: "Main", value: 0 },
                      { display: "Participants", value: 1 },
                      { display: "Lines", value: 2 }
                    ]}
                  />

                  <Typography className={classes.padding} />
                  {/* Render all tabs but hide inactive ones to maintain state/cache */}
                  <div style={{ display: tabValue === 0 ? 'block' : 'none' }}>
                    {loadedTabs.has(0) && (
                      <VendorAgreementMain 
                        data={agreementData} 
                        isLoading={isLoadingMainData || loadingTabs.has(0)} 
                      />
                    )}
                  </div>
                  <div style={{ display: tabValue === 1 ? 'block' : 'none' }}>
                    {loadedTabs.has(1) && (
                      <VendorAgreementParticipants
                        headers={headers}
                        fetchInitialData={props.fetchInitialData}
                        key={`participants-${tableRefreshKey}`}
                      />
                    )}
                  </div>
                  <div style={{ display: tabValue === 2 ? 'block' : 'none' }}>
                    {loadedTabs.has(2) && (
                      <VendorAgreementLinesTable
                        headers={headers}
                        agreementData={agreementData}
                        fetchInitialData={props.fetchInitialData}
                        key={`lines-${tableRefreshKey}`}
                      />
                    )}
                  </div>
                </div>
              </>
            </div>
          </CardContent>
        </Card>
      )}
      {showTable && (isLoadingMainData || loadingTabs.has(0)) && (
        <div style={{ margin: '1rem auto', display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <LoadingCircle />
        </div>
      )}
    </div>
  )
}


